import { ReactNode, useEffect, useRef, useState } from "react";
import {
  CBQChoice,
  CBQSavedResponse,
  CBQStudentQuestion,
} from "../../api/Quiz/cbqStudentTypes";
import { CBQResponseBody } from "../../api/Quiz/SaveCBQResponse";

// Renders one CBQ question with the right input for its type and reports
// answer changes via onSave. Local state keeps the input responsive; the
// parent persists to the server.
const CbqQuestion = ({
  question,
  saved,
  disabled,
  onSave,
  belowPrompt,
}: {
  question: CBQStudentQuestion;
  saved?: CBQSavedResponse;
  disabled?: boolean;
  onSave: (body: CBQResponseBody) => void;
  // Optional content rendered directly under the question title.
  belowPrompt?: ReactNode;
}) => {
  const type = question.type;
  const opts = question.optionsJson ?? {};

  // ---- mcq ----
  const choices = (opts.choices as CBQChoice[]) ?? [];
  const [mcq, setMcq] = useState<string>(saved?.mcq?.answer ?? "");

  // ---- multi_response ----
  const [multi, setMulti] = useState<string[]>(
    saved?.multiResponse?.answers ?? []
  );

  // ---- dropdown ----
  const blanks = (opts.blanks as Record<string, string[]>) ?? {};
  const blankNames = Object.keys(blanks).sort(
    (a, b) => Number(a.split("_")[1]) - Number(b.split("_")[1])
  );
  const [dropdown, setDropdown] = useState<Record<string, string>>(
    saved?.dropdown?.blanks ?? {}
  );

  // ---- ordering ----
  const items = (opts.items as string[]) ?? [];
  const slots = (opts.slots as string[]) ?? [];
  const [placements, setPlacements] = useState<Record<string, string>>(
    saved?.dragDropOrdering?.placements ?? {}
  );

  // ---- classification ----
  const zones = (opts.zones as string[]) ?? [];
  const [assignment, setAssignment] = useState<Record<string, string>>(() => {
    // saved zones come back as { zone: [items] }; flatten to item -> zone.
    const z = (saved?.dragDropClassification?.zones ?? {}) as Record<
      string,
      string[]
    >;
    const flat: Record<string, string> = {};
    Object.entries(z).forEach(([zone, list]) =>
      (list ?? []).forEach((it) => (flat[it] = zone))
    );
    return flat;
  });

  // ---- numeric ----
  const allowDecimals = !!opts.allowDecimals;
  const prefix = (opts.prefix as string) ?? "";
  const [numeric, setNumeric] = useState<string>(
    saved?.numeric?.value !== undefined ? String(saved.numeric.value) : ""
  );

  // The useState initialisers above only run on mount, so a question that
  // mounts before its saved response is available keeps showing blank inputs.
  // Re-seed when the answer first arrives (or when this slot is reused for a
  // different question). Deliberately not on every `saved` identity change:
  // each save echoes a new object back, and re-seeding then would clobber
  // whatever the student is part-way through typing.
  const seededFor = useRef("");
  useEffect(() => {
    const stamp = `${question.id}:${saved ? "1" : "0"}`;
    if (seededFor.current === stamp) return;
    seededFor.current = stamp;

    setMcq(saved?.mcq?.answer ?? "");
    setMulti(saved?.multiResponse?.answers ?? []);
    setDropdown(saved?.dropdown?.blanks ?? {});
    setPlacements(saved?.dragDropOrdering?.placements ?? {});
    setNumeric(
      saved?.numeric?.value !== undefined ? String(saved.numeric.value) : ""
    );

    const z = (saved?.dragDropClassification?.zones ?? {}) as Record<
      string,
      string[]
    >;
    const flat: Record<string, string> = {};
    Object.entries(z).forEach(([zone, list]) =>
      (list ?? []).forEach((it) => (flat[it] = zone))
    );
    setAssignment(flat);
  }, [question.id, saved]);

  const saveMcq = (answer: string) => {
    setMcq(answer);
    onSave({ type, mcq: { answer } });
  };

  const toggleMulti = (key: string) => {
    const next = multi.includes(key)
      ? multi.filter((k) => k !== key)
      : [...multi, key];
    setMulti(next);
    onSave({ type, multiResponse: { answers: next } });
  };

  const setBlank = (name: string, value: string) => {
    const next = { ...dropdown, [name]: value };
    setDropdown(next);
    onSave({ type, dropdown: { blanks: next } });
  };

  // Drag an item into a slot (an item can occupy only one slot).
  const dropToSlot = (slot: string, item: string) => {
    const next = { ...placements };
    Object.keys(next).forEach((sl) => {
      if (next[sl] === item) delete next[sl];
    });
    next[slot] = item;
    setPlacements(next);
    onSave({ type, dragDropOrdering: { placements: next } });
  };
  // Drag an item back to the Options pool to take it out of its slot.
  const removeFromSlot = (item: string) => {
    const next = { ...placements };
    Object.keys(next).forEach((sl) => {
      if (next[sl] === item) delete next[sl];
    });
    setPlacements(next);
    onSave({ type, dragDropOrdering: { placements: next } });
  };

  const setItemZone = (item: string, zone: string) => {
    const next = { ...assignment, [item]: zone };
    setAssignment(next);
    const grouped: Record<string, string[]> = {};
    Object.entries(next).forEach(([it, z]) => {
      if (!z) return;
      (grouped[z] ??= []).push(it);
    });
    onSave({ type, dragDropClassification: { zones: grouped } });
  };

  const saveNumeric = () => {
    if (numeric.trim() === "") return;
    onSave({ type, numeric: { value: Number(numeric) } });
  };

  // Render the prompt with [blank_n] tokens replaced by inline dropdowns.
  const renderDropdownPrompt = () => {
    const used = new Set<string>();
    const parts = question.prompt.split(/(\[blank_\d+\])/g);
    const nodes = parts.map((part, i) => {
      const m = part.match(/^\[(blank_\d+)\]$/);
      if (m) {
        const name = m[1];
        used.add(name);
        return (
          <select
            key={`b-${i}`}
            value={dropdown[name] ?? ""}
            disabled={disabled}
            onChange={(e) => setBlank(name, e.target.value)}
            className="mx-1 my-0.5 inline-block align-middle border-2 border-gray-400 bg-gray-100 text-black rounded px-2 py-1 text-sm font-medium focus:outline-none focus:border-black"
          >
            <option value="">— select —</option>
            {(blanks[name] ?? []).map((o) => (
              <option key={o} value={o}>
                {o}
              </option>
            ))}
          </select>
        );
      }
      return <span key={`t-${i}`}>{part}</span>;
    });
    // Blanks defined but not referenced in the sentence still need an input.
    const leftover = blankNames.filter((n) => !used.has(n));
    return (
      <>
        {nodes}
        {leftover.length > 0 && (
          <span className="block space-y-2 mt-3">
            {leftover.map((name) => (
              <span key={name} className="flex items-center gap-2">
                <span className="text-sm font-semibold text-gray-600">
                  {name}
                </span>
                <select
                  className="border border-black/20 rounded-lg px-3 py-2"
                  value={dropdown[name] ?? ""}
                  disabled={disabled}
                  onChange={(e) => setBlank(name, e.target.value)}
                >
                  <option value="">— select —</option>
                  {(blanks[name] ?? []).map((o) => (
                    <option key={o} value={o}>
                      {o}
                    </option>
                  ))}
                </select>
              </span>
            ))}
          </span>
        )}
      </>
    );
  };

  return (
    <div className="border border-black/15 rounded-xl p-5 space-y-4">
      <div>
        {type === "dropdown" ? (
          <p className="font-semibold text-bruntBlack leading-9">
            {renderDropdownPrompt()}
          </p>
        ) : (
          <p className="font-semibold text-bruntBlack">
            {question.prompt}
          </p>
        )}
        <p className="text-xs text-lightGray mt-1">{question.points} pts</p>
        {belowPrompt ? <div className="mt-3">{belowPrompt}</div> : null}
      </div>

      {type === "mcq" && (
        <div className="space-y-3">
          {choices.map((c) => {
            const selected = mcq === c.key;
            return (
              <div key={c.key} className="flex items-center gap-3">
                <span className="font-semibold text-burntBlack w-5 shrink-0">
                  {c.key}
                </span>
                <button
                  type="button"
                  disabled={disabled}
                  onClick={() => saveMcq(c.key)}
                  className={`flex-1 text-left px-4 py-3 rounded-lg border-2 transition-colors ${
                    selected
                      ? "bg-[#FFE7C2] border-[#E8890C]"
                      : "bg-white border-gray-300 hover:border-gray-400"
                  }`}
                >
                  {c.text}
                </button>
              </div>
            );
          })}
        </div>
      )}

      {type === "multi_response" && (
        <div className="space-y-3">
          {choices.map((c) => {
            const selected = multi.includes(c.key);
            return (
              <div key={c.key} className="flex items-center gap-3">
                <span className="font-semibold text-burntBlack w-5 shrink-0">
                  {c.key}
                </span>
                <button
                  type="button"
                  disabled={disabled}
                  onClick={() => toggleMulti(c.key)}
                  className={`flex-1 text-left px-4 py-3 rounded-lg border-2 transition-colors ${
                    selected
                      ? "bg-[#FFE7C2] border-[#E8890C]"
                      : "bg-white border-gray-300 hover:border-gray-400"
                  }`}
                >
                  {c.text}
                </button>
              </div>
            );
          })}
        </div>
      )}

      {type === "drag_drop_ordering" && (
        <div className="space-y-4">
          <div>
            <p className="font-semibold text-black text-sm mb-1">Options</p>
            <div
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault();
                const it = e.dataTransfer.getData("text/plain");
                if (it && !disabled) removeFromSlot(it);
              }}
              className="bg-white border border-black rounded-lg p-3 flex flex-wrap gap-2 min-h-[52px]"
            >
              {items.filter((i) => !Object.values(placements).includes(i))
                .length === 0 ? (
                <span className="text-xs text-gray-400">All items placed</span>
              ) : (
                items
                  .filter((i) => !Object.values(placements).includes(i))
                  .map((item) => (
                    <div
                      key={item}
                      draggable={!disabled}
                      onDragStart={(e) =>
                        e.dataTransfer.setData("text/plain", item)
                      }
                      className="rounded-md bg-[#DAE2E5] text-black border border-black px-3 py-2 cursor-grab select-none"
                    >
                      {item}
                    </div>
                  ))
              )}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            {slots.map((slot) => {
              const placed = placements[slot];
              return (
                <div key={slot} className="flex flex-col">
                  <span className="text-sm font-semibold text-black mb-1">
                    {slot}
                  </span>
                  <div
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={(e) => {
                      e.preventDefault();
                      const it = e.dataTransfer.getData("text/plain");
                      if (it && !disabled) dropToSlot(slot, it);
                    }}
                    className="w-full min-h-[150px] bg-[#DAE2E5] border border-black rounded-lg p-3 flex items-start justify-start content-start flex-wrap gap-2"
                  >
                    {placed ? (
                      <div
                        draggable={!disabled}
                        onDragStart={(e) =>
                          e.dataTransfer.setData("text/plain", placed)
                        }
                        className="rounded-md bg-white text-black border border-black px-3 py-2 cursor-grab select-none"
                      >
                        {placed}
                      </div>
                    ) : null}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {type === "drag_drop_classification" && (
        <div className="space-y-4">
          <div>
            <p className="font-semibold text-black text-sm mb-1">Options</p>
            <div
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault();
                const it = e.dataTransfer.getData("text/plain");
                if (it && !disabled) setItemZone(it, "");
              }}
              className="bg-white border border-black rounded-lg p-3 flex flex-wrap gap-2 min-h-[52px]"
            >
              {items.filter((i) => !assignment[i]).length === 0 ? (
                <span className="text-xs text-gray-400">All items placed</span>
              ) : (
                items
                  .filter((i) => !assignment[i])
                  .map((item) => (
                    <div
                      key={item}
                      draggable={!disabled}
                      onDragStart={(e) =>
                        e.dataTransfer.setData("text/plain", item)
                      }
                      className="rounded-md bg-[#DAE2E5] text-black border border-black px-3 py-2 cursor-grab select-none"
                    >
                      {item}
                    </div>
                  ))
              )}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            {zones.map((zone) => (
              <div key={zone} className="flex flex-col">
                <span className="text-sm font-semibold text-black mb-1">
                  {zone}
                </span>
                <div
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => {
                    e.preventDefault();
                    const it = e.dataTransfer.getData("text/plain");
                    if (it && !disabled) setItemZone(it, zone);
                  }}
                  className="w-full min-h-[150px] bg-[#DAE2E5] border border-black rounded-lg p-3 flex items-start justify-start content-start flex-wrap gap-2"
                >
                  {items
                    .filter((i) => assignment[i] === zone)
                    .map((item) => (
                      <div
                        key={item}
                        draggable={!disabled}
                        onDragStart={(e) =>
                          e.dataTransfer.setData("text/plain", item)
                        }
                        className="rounded-md bg-white text-black border border-black px-3 py-2 cursor-grab select-none"
                      >
                        {item}
                      </div>
                    ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {type === "numeric" && (
        <div className="flex items-center gap-2">
          {prefix ? <span className="text-gray-600">{prefix}</span> : null}
          <input
            type="number"
            step={allowDecimals ? "any" : "1"}
            className="border border-black/20 rounded-lg px-3 py-2 w-48"
            value={numeric}
            disabled={disabled}
            onChange={(e) => setNumeric(e.target.value)}
            onBlur={saveNumeric}
          />
        </div>
      )}
    </div>
  );
};

export default CbqQuestion;
